import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { SUBJECTS } from '../../data/subjects';
import { useAppRole } from '../../hooks/useAppRole';
import { readAdminToken } from '../../lib/adminToken';
import { readTeacherSession } from '../../lib/teacherSession';
import { START_NEW_SUBJECT_CONVERSATION_STATE } from '../../lib/subjectNavigation';

interface PublicHeaderProps {
  activeSubjectRoute?: string;
  title?: string;
}

export default function PublicHeader({ activeSubjectRoute, title }: PublicHeaderProps) {
  const navigate = useNavigate();
  const { isTeacher, useLiveAuth, logoutToStudent } = useAppRole();
  const [fagOpen, setFagOpen] = useState(false);
  const [profilOpen, setProfilOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const fagButtonRef = useRef<HTMLButtonElement>(null);
  const profilButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setFagOpen(false);
        setProfilOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    setFagOpen(false);
    setProfilOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setProfilOpen(false);
    void logoutToStudent().then(() => {
      navigate('/', { replace: true });
    });
  };

  const isClassroom =
    location.pathname === '/klassevaerelset' || location.pathname === '/klasse';
  const isTeacherClassroom = location.pathname === '/til-klassevaerelset';
  const isTeacherPrep = location.pathname === '/laererforberedelse';
  const isTeacherLogin = location.pathname === '/login';
  const isOperatorLogin = location.pathname === '/admin-gymgpt-login';
  const isProfile = location.pathname === '/profil';
  const hideAuthCta = isTeacherLogin || isOperatorLogin;
  const showTeacherProfile =
    Boolean(readTeacherSession()) || (!useLiveAuth && isTeacher && !readAdminToken());
  const showLoginCta = !hideAuthCta && !showTeacherProfile;
  const showProfileCta = !hideAuthCta && showTeacherProfile;
  const activeSubject = SUBJECTS.find((subject) => subject.route === activeSubjectRoute);
  const headerTitle =
    title
    ?? activeSubject?.label
    ?? (isTeacherClassroom ? 'Til klasseværelset' : isTeacherPrep ? 'Lærerforberedelsen' : isProfile ? 'Profil' : null);
  const showSubjectHeaderLayout = headerTitle !== null;
  const showMarketingHeader = !showSubjectHeaderLayout;
  const useCompactAuthCta = showSubjectHeaderLayout;

  const classroomCta = (
    <Link
      to="/klassevaerelset"
      className="public-nav-text"
      aria-current={isClassroom ? 'page' : undefined}
      style={{
        color: 'var(--text-heading)',
        fontWeight: 600,
      }}
    >
      Klasse
    </Link>
  );

  const authCta = showProfileCta ? (
    <div className="public-profile-menu-wrap" style={{ position: 'relative' }}>
      <button
        ref={profilButtonRef}
        type="button"
        className={
          'public-nav-login-cta public-nav-profile-trigger'
          + (useCompactAuthCta ? ' public-nav-auth-icon-cta' : '')
        }
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setFagOpen(false);
          setProfilOpen((open) => !open);
        }}
        aria-haspopup="menu"
        aria-expanded={profilOpen}
        aria-label="Profil"
        aria-current={isProfile ? 'page' : undefined}
      >
        {useCompactAuthCta ? null : 'Profil'}
        <NavProfileChevron open={profilOpen} />
      </button>
      {profilOpen ? (
        <div
          role="menu"
          className="public-subject-menu public-profile-menu"
          aria-label="Profil"
        >
          <Link
            to="/"
            role="menuitem"
            className={`public-profile-menu-item${location.pathname === '/' ? ' is-active' : ''}`}
            onClick={() => setProfilOpen(false)}
          >
            Forside
          </Link>
          <Link
            to="/profil"
            role="menuitem"
            className={`public-profile-menu-item${isProfile ? ' is-active' : ''}`}
            onClick={() => setProfilOpen(false)}
          >
            Indstillinger
          </Link>
          <button
            type="button"
            role="menuitem"
            className="public-profile-menu-item"
            onClick={handleLogout}
          >
            Log ud
          </button>
        </div>
      ) : null}
    </div>
  ) : showLoginCta ? (
    <Link
      to="/login"
      className={'public-nav-login-cta' + (useCompactAuthCta ? ' public-nav-auth-icon-cta' : '')}
      aria-label="Log ind"
    >
      {useCompactAuthCta ? <NavLoginLockIcon /> : 'Log ind'}
    </Link>
  ) : null;

  return (
    <div className="public-header-wrap" style={{ margin: '0 10px', position: 'relative', zIndex: 20 }}>
      <header
        className={`public-header${showSubjectHeaderLayout ? ' public-header--subject' : ' public-header--marketing'}`}
        style={{
          background: 'var(--surface-card)',
          borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
          boxShadow: 'var(--shadow-header)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: 'var(--font-body)',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <Link className="public-brand" to="/" style={{ textDecoration: 'none' }}>
          <img src="/assets/logo.png" alt="GymGPT" className="public-brand-logo" />
        </Link>
        {headerTitle ? (
          <p className="public-header-subject-title" style={{ fontWeight: 600, color: 'var(--text-heading)' }}>
            {headerTitle}
          </p>
        ) : null}
        <nav
          ref={navRef}
          className="public-nav"
          aria-label="Hovednavigation"
          onKeyDown={(event) => {
            if (event.key !== 'Escape') {
              return;
            }
            if (profilOpen) {
              event.preventDefault();
              setProfilOpen(false);
              profilButtonRef.current?.focus();
              return;
            }
            if (fagOpen) {
              event.preventDefault();
              setFagOpen(false);
              fagButtonRef.current?.focus();
            }
          }}
        >
          <div style={{ position: 'relative' }}>
            <button
              ref={fagButtonRef}
              type="button"
              className="public-nav-text"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setProfilOpen(false);
                setFagOpen((open) => !open);
              }}
              aria-haspopup="true"
              aria-expanded={fagOpen}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: activeSubjectRoute ? 'var(--blue-600)' : 'var(--text-heading)',
                fontFamily: 'var(--font-body)',
                fontWeight: 600,
              }}
            >
              Fag
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transition: 'transform 160ms ease',
                  transform: fagOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
                aria-hidden
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {fagOpen ? (
              <div
                role="menu"
                className="public-subject-menu"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 14px)',
                  left: -12,
                  minWidth: 200,
                  background: 'var(--surface-card)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-float)',
                  padding: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 30,
                }}
              >
                {SUBJECTS.map((subject) => {
                  const active = activeSubjectRoute === subject.route;
                  return (
                    <Link
                      key={subject.id}
                      to={subject.route}
                      state={START_NEW_SUBJECT_CONVERSATION_STATE}
                      role="menuitem"
                      style={{
                        padding: '11px 16px',
                        borderRadius: 'var(--radius-sm)',
                        color: active ? 'var(--blue-600)' : 'var(--text-heading)',
                        background: active ? 'var(--blue-50)' : 'transparent',
                        fontSize: 16,
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.background = 'var(--blue-50)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      {subject.label}
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </div>
          <Link
            to="/om-gymgpt"
            className="public-nav-text"
            style={{
              color: 'var(--text-heading)',
              fontWeight: 600,
            }}
          >
            Om GymGPT
          </Link>
          {showMarketingHeader ? (
            <div className="public-nav-classroom-slot">
              {authCta ? <div className="public-nav-classroom-slot-auth">{authCta}</div> : null}
              {classroomCta}
            </div>
          ) : (
            <>
              {classroomCta}
              {authCta}
            </>
          )}
        </nav>
      </header>
    </div>
  );
}

function NavProfileChevron({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="public-nav-profile-chevron"
      style={{
        transition: 'transform 160ms ease',
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function NavLoginLockIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
